import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PageChangeEvent } from '@progress/kendo-angular-grid';
import { first } from 'rxjs';
import { IBaseSearchRequest, IGridResult, IGridState, PageSize } from 'src/app/core/models/base';
import { buildPagingQueryParams, pageFromSkip, parsePagingQuery } from 'src/app/shared/helpers/grid-query-params';
import { ListQueryStateService } from 'src/app/shared/services/list-query-state.service';
import { IContact, Salutations } from '../models/contact';
import { ContactService } from '../services/contact.service';
import { buildContactsSearchRequest } from './contacts-query';

@UntilDestroy()
@Component({
    selector: 'app-contacts',
    templateUrl: './contacts.component.html',
    styleUrls: ['./contacts.component.css'],
    standalone: false
})
export class ContactsComponent implements OnInit {

  constructor(
    private contactService: ContactService,
    private activeRoute: ActivatedRoute,
    private router: Router,
    private listQueryState: ListQueryStateService
  ) { }

  gridState: IGridState = {
    pageSize: 25,
    skip: 0,
  };

  contacts: IGridResult<IContact[]>;

  searchRequest: IBaseSearchRequest = {
    pageNumber: 1,
    pageSize: 25,
    orderBy:['lastName','CompanyName']
  }

  get pageSizes() {
    return PageSize;
  }

  get Salutations(){
    return Salutations;
  }

  filterForm : UntypedFormGroup;

  showResetButton = false;

  ngOnInit(): void {
    this.contacts = this.activeRoute.snapshot.data['contacts'] as IGridResult<IContact[]>;

    // Paging and keyword come from the URL. The resolver already loaded this
    // exact page, so no fetch is needed here.
    const q = this.activeRoute.snapshot.queryParamMap;
    const paging = parsePagingQuery(q);
    this.gridState = { skip: paging.skip, pageSize: paging.pageSize };
    this.searchRequest = buildContactsSearchRequest(q);

    this.filterForm = new UntypedFormGroup({
      keyword: new UntypedFormControl(this.searchRequest.keyword ?? null),
    })
    this.showResetButton = !!this.searchRequest.keyword;

    this.listQueryState.remember('contacts', this.activeRoute.snapshot.queryParams);
  }

  updateList(){
    this.contactService.listContacts(this.searchRequest)
    .pipe(first(), untilDestroyed(this))
    .subscribe(response => {
      this.contacts.data = response.data;
      this.contacts.total = response.totalCount;
    })
  }

  /** Writes paging and keyword to the URL and remembers them for the detail view's back navigation. */
  private writeUrl(): void {
    const params: Params = {
      ...(this.searchRequest.keyword ? { keyword: this.searchRequest.keyword } : {}),
      ...buildPagingQueryParams(this.gridState),
    };
    this.router.navigate([], {
      relativeTo: this.activeRoute,
      queryParams: params,
      replaceUrl: true
    });
    this.listQueryState.remember('contacts', params);
  }

  private resetPaging(): void {
    this.gridState.skip = 0;
    this.searchRequest.pageNumber = 1;
  }

  onPageSizeChange(pageSize: number) {
    this.onPageChange({ skip: 0, take: pageSize });
  }

  onGridReload() {
    this.updateList();
  }

  onPageChange({ skip, take }: PageChangeEvent) {
    this.gridState.skip = skip;
    this.gridState.pageSize = take;

    this.searchRequest.pageNumber = pageFromSkip(skip, take);
    this.searchRequest.pageSize = take;

    this.writeUrl();
    this.updateList();
  }

  onSubmit(){
    this.searchRequest.keyword = this.filterForm.value.keyword;
    this.resetPaging();
    this.writeUrl();
    this.updateList();
    this.showResetButton = true;
  }

  onReset(){
    this.filterForm.reset();
    this.showResetButton = false;
    this.searchRequest.keyword = null;
    this.resetPaging();
    this.writeUrl();
    this.updateList();
  }

}
